import { replaceUrl } from 'components/helpers/history';
import type { ParsedUrlQuery } from 'querystring';

import type { GeoPoint } from '../map/types';
import { DEFAULT_LISTING_SORT_SLUG, isListingSortSlug, type ListingSortSlug } from './sortOptions';

const UUID_PATTERN =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/iu;

const isGeoPoint = (value: unknown): value is GeoPoint =>
  typeof value === 'object' &&
  value !== null &&
  'lat' in value &&
  typeof value.lat === 'number' &&
  Number.isFinite(value.lat) &&
  value.lat >= -90 &&
  value.lat <= 90 &&
  'lon' in value &&
  typeof value.lon === 'number' &&
  Number.isFinite(value.lon) &&
  value.lon >= -180 &&
  value.lon <= 180;

export const isUuid = (value: string): boolean => UUID_PATTERN.test(value);

export interface PropertySearchQuery {
  bedrooms?: number | null;
  bathrooms?: number | null;
  canSelfTour?: boolean | null;
  minListPrice?: number | null;
  maxListPrice?: number | null;
  minSquareFootage?: number | null;
  maxSquareFootage?: number | null;
  hasSingleStory?: boolean | null;
  hasPool?: boolean | null;
  singleFamily?: boolean | null;
  condo?: boolean | null;
  townhome?: boolean | null;
  zoneIds?: string[] | null;
  minYearBuilt?: number | null;
  maxYearBuilt?: number | null;
  vertices?: GeoPoint[] | null;
  market?: string | null;
  noHoas?: boolean | null;
  minLotSize?: number | null;
  maxLotSize?: number | null;
  preview_aid?: string | null;
  sort?: ListingSortSlug | null;
  /** One-based results page, as it appears in the URL. */
  page?: number | null;
}

export const normalizeSearchVertices = (vertices?: GeoPoint[] | null): GeoPoint[] | null => {
  if (!vertices || vertices.length > 256) {
    return null;
  }
  if (!vertices.every(isGeoPoint)) {
    return null;
  }

  const first = vertices[0];
  const last = vertices.at(-1);
  if (!first || !last) {
    return null;
  }
  const isClosed = first.lat === last.lat && first.lon === last.lon;
  if (vertices.length < (isClosed ? 4 : 3)) {
    return null;
  }
  if (isClosed) {
    return vertices;
  }
  return vertices.length < 256 ? [...vertices, first] : null;
};

const safeIntegerOrNull = (value: number | null | undefined) =>
  value == null || Number.isSafeInteger(value) ? value : null;

export const normalizeSearchQuery = (query: PropertySearchQuery): PropertySearchQuery => {
  const normalized = { ...query };
  if (normalized.vertices?.length) {
    normalized.vertices = normalizeSearchVertices(normalized.vertices);
  }
  // Default sort drops out of the URL so region pages keep one canonical form.
  if (normalized.sort === DEFAULT_LISTING_SORT_SLUG) {
    normalized.sort = null;
  }
  // The first page drops out for the same reason, and a filter change that omits
  // `page` resets to it rather than stranding the visitor on a page that may no
  // longer exist in the narrowed result set.
  const requestedPage = safeIntegerOrNull(normalized.page);
  normalized.page = requestedPage != null && requestedPage >= 2 ? requestedPage : null;
  normalized.bedrooms = safeIntegerOrNull(normalized.bedrooms);
  normalized.minListPrice = safeIntegerOrNull(normalized.minListPrice);
  normalized.maxListPrice = safeIntegerOrNull(normalized.maxListPrice);
  normalized.minSquareFootage = safeIntegerOrNull(normalized.minSquareFootage);
  normalized.maxSquareFootage = safeIntegerOrNull(normalized.maxSquareFootage);
  normalized.minYearBuilt = safeIntegerOrNull(normalized.minYearBuilt);
  normalized.maxYearBuilt = safeIntegerOrNull(normalized.maxYearBuilt);
  normalized.minLotSize = safeIntegerOrNull(normalized.minLotSize);
  normalized.maxLotSize = safeIntegerOrNull(normalized.maxLotSize);
  if (normalized.maxListPrice === 0) {
    normalized.maxListPrice = null;
  }
  const hasPropertyType =
    normalized.singleFamily !== undefined ||
    normalized.condo !== undefined ||
    normalized.townhome !== undefined;
  if (hasPropertyType && !normalized.singleFamily && !normalized.condo && !normalized.townhome) {
    normalized.singleFamily = true;
    normalized.condo = true;
    normalized.townhome = true;
  }
  if (
    normalized.minListPrice != null &&
    normalized.maxListPrice != null &&
    normalized.minListPrice > normalized.maxListPrice
  ) {
    [normalized.minListPrice, normalized.maxListPrice] = [
      normalized.maxListPrice,
      normalized.minListPrice,
    ];
  }
  if (
    normalized.minSquareFootage != null &&
    normalized.maxSquareFootage != null &&
    normalized.minSquareFootage > normalized.maxSquareFootage
  ) {
    [normalized.minSquareFootage, normalized.maxSquareFootage] = [
      normalized.maxSquareFootage,
      normalized.minSquareFootage,
    ];
  }
  if (
    normalized.minYearBuilt != null &&
    normalized.maxYearBuilt != null &&
    normalized.minYearBuilt > normalized.maxYearBuilt
  ) {
    [normalized.minYearBuilt, normalized.maxYearBuilt] = [
      normalized.maxYearBuilt,
      normalized.minYearBuilt,
    ];
  }
  if (
    normalized.minLotSize != null &&
    normalized.maxLotSize != null &&
    normalized.minLotSize > normalized.maxLotSize
  ) {
    [normalized.minLotSize, normalized.maxLotSize] = [normalized.maxLotSize, normalized.minLotSize];
  }
  for (const [key, value] of Object.entries(normalized)) {
    if (value === undefined) {
      delete normalized[key as keyof PropertySearchQuery];
    }
  }
  return normalized;
};

export const updateQueryParams = (
  input: PropertySearchQuery,
  opts: { seedFromUrl?: boolean } = {},
) => {
  const queryParams = buildQueryParams(input, opts);
  replaceUrl(`${window.location.pathname}?${queryParams}`);
};

export const buildQueryParams = (
  input: PropertySearchQuery,
  { seedFromUrl = true }: { seedFromUrl?: boolean } = {},
): URLSearchParams => {
  // Seeding from the current URL lets in-place filter updates preserve unrelated
  // params. Callers navigating to a different search (e.g. switching saved
  // searches) must opt out so stale params don't leak into the canonical query.
  const urlParams = new URLSearchParams(seedFromUrl ? window.location.search : '');
  const normalizedInput = normalizeSearchQuery(input);
  for (const [alias, canonical] of PARAM_ALIASES) {
    if (Object.hasOwn(normalizedInput, canonical)) {
      urlParams.delete(alias);
    }
  }
  const hasVertices = Boolean(normalizedInput.vertices?.length);
  const hasZoneIds = Boolean(normalizedInput.zoneIds?.length);
  if (hasVertices || ('vertices' in normalizedInput && !hasZoneIds)) {
    urlParams.delete('zoneIds');
  }
  if (hasZoneIds || 'vertices' in normalizedInput) {
    for (const param of Array.from(urlParams.keys())) {
      if (param.startsWith('vertex_')) {
        urlParams.delete(param);
      }
    }
  }

  for (const [key, value] of Object.entries(normalizedInput)) {
    if (key === 'zoneIds') {
      if (!hasVertices && Array.isArray(value) && value.length > 0) {
        urlParams.set(key, value.join(','));
      } else {
        urlParams.delete(key);
      }
    } else if (key === 'vertices') {
      if (Array.isArray(value)) {
        value.forEach((vertex: GeoPoint, index: number) => {
          urlParams.set(`vertex_${index}`, encodeURI(JSON.stringify(vertex)));
        });
      }
    } else if (key === 'market') {
      if (typeof value === 'string' && value.length > 0) {
        urlParams.set(key, value);
      } else {
        urlParams.delete(key);
      }
    } else if (typeof value === 'boolean') {
      urlParams.set(key, String(value));
    } else if (value) {
      urlParams.set(key, String(value));
    } else {
      urlParams.delete(key);
    }
  }
  return urlParams;
};

export const buildVerticesQueryParams = (vertices: GeoPoint[]): URLSearchParams => {
  const urlParams = new URLSearchParams(window.location.search);
  const normalizedVertices = normalizeSearchVertices(vertices);
  if (!normalizedVertices) {
    return urlParams;
  }
  urlParams.delete('zoneIds');
  for (const param of Array.from(urlParams.keys())) {
    if (param.startsWith('vertex_')) {
      urlParams.delete(param);
    }
  }
  normalizedVertices.forEach((vertex, index) => {
    urlParams.set(`vertex_${index}`, encodeURI(JSON.stringify(vertex)));
  });
  return urlParams;
};

export const buildAddressPreviewParams = (addressId: string): URLSearchParams => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('preview_aid', addressId);
  return urlParams;
};

export const removeAddressPreviewParams = (): URLSearchParams => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete('preview_aid');
  return urlParams;
};

// URL param carrying the applied saved search's id. Application is sometimes a
// hard navigation (cross-market / zone / polygon), so the id must survive SSR
// to restore the "active saved search" state on the destination page.
export const SAVED_SEARCH_ID_PARAM = 'ssid';

// Maps URL param aliases to their canonical PropertySearchQuery field names
const PARAM_ALIASES = new Map<string, keyof PropertySearchQuery>([
  ['minBeds', 'bedrooms'],
  ['minBaths', 'bathrooms'],
  ['minPrice', 'minListPrice'],
  ['maxPrice', 'maxListPrice'],
  ['singleStory', 'hasSingleStory'],
  ['mustHavePool', 'hasPool'],
]);

const NUMBER_PARAMS = [
  'bedrooms',
  'bathrooms',
  'minListPrice',
  'maxListPrice',
  'minSquareFootage',
  'maxSquareFootage',
  'minYearBuilt',
  'maxYearBuilt',
  'minLotSize',
  'maxLotSize',
  'page',
] as const satisfies readonly (keyof PropertySearchQuery)[];
type NumberParam = (typeof NUMBER_PARAMS)[number];
const NUMBER_PARAM_SET = new Set<string>(NUMBER_PARAMS);

const BOOLEAN_PARAMS = [
  'canSelfTour',
  'hasSingleStory',
  'hasPool',
  'singleFamily',
  'condo',
  'townhome',
  'noHoas',
] as const satisfies readonly (keyof PropertySearchQuery)[];
type BooleanParam = (typeof BOOLEAN_PARAMS)[number];
const BOOLEAN_PARAM_SET = new Set<string>(BOOLEAN_PARAMS);

const isNumberParam = (key: string): key is NumberParam => NUMBER_PARAM_SET.has(key);
const isBooleanParam = (key: string): key is BooleanParam => BOOLEAN_PARAM_SET.has(key);

const parseVertex = (value: string): GeoPoint | null => {
  try {
    const parsed: unknown = JSON.parse(decodeURI(value));
    return isGeoPoint(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const parseQueryParams = (urlParams: ParsedUrlQuery): PropertySearchQuery => {
  const queryParams: PropertySearchQuery = {};
  const vertices: { index: number; point: GeoPoint }[] = [];
  let invalidVertices = false;

  Object.entries(urlParams).forEach(([key, value]) => {
    if (key === SAVED_SEARCH_ID_PARAM) {
      return;
    }
    const alias = PARAM_ALIASES.get(key);
    if (alias && Object.hasOwn(urlParams, alias)) {
      return;
    }
    const normalizedKey = alias ?? key;
    if (normalizedKey === 'zoneIds' && typeof value === 'string') {
      const zoneIds = value.split(',').filter(isUuid);
      if (zoneIds.length) {
        queryParams.zoneIds = zoneIds;
      }
    } else if (key.startsWith('vertex_')) {
      const index = /^vertex_(0|[1-9]\d*)$/u.exec(key)?.[1];
      const vertex = typeof value === 'string' ? parseVertex(value) : null;
      if (index == null || vertex == null) {
        invalidVertices = true;
      } else {
        vertices.push({ index: Number(index), point: vertex });
      }
    } else if (typeof value === 'string' && isNumberParam(normalizedKey)) {
      if (value.trim() === '') {
        return;
      }
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        queryParams[normalizedKey] = parsed;
      }
    } else if (typeof value === 'string' && isBooleanParam(normalizedKey)) {
      if (value === 'true' || value === 'false') {
        queryParams[normalizedKey] = value === 'true';
      }
    } else if (normalizedKey === 'market' && typeof value === 'string') {
      queryParams.market = value;
    } else if (normalizedKey === 'preview_aid' && typeof value === 'string' && isUuid(value)) {
      queryParams.preview_aid = value;
    } else if (normalizedKey === 'sort') {
      // Set the key either way: an unrecognized value must be dropped from the
      // URL too, and the serializer only deletes params it sees.
      queryParams.sort = typeof value === 'string' && isListingSortSlug(value) ? value : null;
    }
  });
  vertices.sort((a, b) => a.index - b.index);
  const hasValidVertexSequence =
    !invalidVertices &&
    vertices.length >= 3 &&
    vertices.length <= 256 &&
    vertices.every(({ index }, position) => index === position);
  queryParams.vertices = hasValidVertexSequence
    ? (normalizeSearchVertices(vertices.map(({ point }) => point)) ?? [])
    : [];
  return normalizeSearchQuery(queryParams);
};

/**
 * Browser-side counterpart to `parseQueryParams`, which takes the router's
 * already-parsed query. Repeated params are rebuilt as arrays so both paths
 * agree on them: `URLSearchParams.get` would silently keep the first value
 * where the router hands the parser an array it rejects.
 */
export const parseQueryParamsFromSearch = (search: string): PropertySearchQuery => {
  const params = new URLSearchParams(search);
  const urlParams: ParsedUrlQuery = {};
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key);
    urlParams[key] = values.length > 1 ? values : (values[0] ?? '');
  }
  return parseQueryParams(urlParams);
};

export const parseSavedSearchId = (urlParams: ParsedUrlQuery): string | null => {
  const value = urlParams[SAVED_SEARCH_ID_PARAM];
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return typeof value === 'string' && value.length > 0 ? value : null;
};
